import React, { FC, useContext, useState } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { pickBy } from 'lodash'
import moment from 'moment'
import { Button } from 'transfix-ui/components/Button'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals'
import SuggestLocation, { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { CardButton } from 'apps/fleet-planner/components/atoms/CardButton'
import { CardButtonGroup } from 'apps/fleet-planner/components/atoms/CardButtonGroup'
import { ReactComponent as TruckAvailabilityIllustration } from 'apps/fleet-planner/components/illustrations/TruckAvailabilityIllustration.svg'
import { RADIUS_OPTIONS } from 'apps/fleet-planner/constants/marketplace'
import { UserContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useTruckAvailabilityForm } from 'apps/fleet-planner/hooks/useTruckAvailabilityModalForm'
import { BookableShipmentSavedSearch } from 'apps/fleet-planner/types'
import { DatePicker } from 'components/forms'
import { ButtonGroup, Select } from 'transfix-ui'
import FORMATS from 'constants/datetime'
import { HAUL_TYPE_MILES } from 'constants/equipment'

const useStyles = makeStyles({
  root: {},
  confirmBox: {
    backgroundColor: brandColors.coolGray1,
    borderRadius: '5px',
    margin: '20px 0',
    padding: '20px',
    display: 'inline-block',
  },
})

const formatDate = (date: string | undefined) => {
  return date ? moment(date).format(FORMATS.monthDateShort) : ''
}

const formatLocation = (suggestion?: ILocationSuggestion, haulType?: string) => {
  let location = ''
  if (haulType === 'local') {
    location = 'Local Hauls'
  } else if (haulType === 'short') {
    location = 'Short Hauls'
  } else if (haulType === 'long') {
    location = 'Long Hauls'
  } else if (suggestion?.city && suggestion?.state) {
    location = `${suggestion.city}, ${suggestion.state}`
  } else if (suggestion?.state) {
    location = suggestion.state
  }
  return location
}

export interface ITruckAvailabilityModalProps extends Pick<IFormModalProps, 'onClose'> {
  onConfirm: (savedSearch: BookableShipmentSavedSearch) => void
  pickupDate?: string
}

export const TruckAvailabilityModal: FC<ITruckAvailabilityModalProps> = ({
  onClose: onCloseProp,
  onConfirm: onConfirmProp,
  pickupDate: pickupDateProp,
}) => {
  const { trackEvent } = useFMSAnalytics()
  const classes = useStyles()
  const { userFlags } = useContext(UserContext)
  const { isMobile, isTablet } = useBreakpointContext()
  const isDesktop = !isMobile && !isTablet

  const [activeStepIdx, setActiveStepIdx] = useState(userFlags.truckAvailabilityDismissed ? 1 : 0)

  const { control, errors, setValue, trigger } = useTruckAvailabilityForm({
    formDefaultValues: { pickupDate: pickupDateProp },
  })

  const pickupValue = useWatch<ILocationSuggestion | undefined>({ name: 'pickup', control })
  const pickupDateValue = useWatch<string | undefined>({ name: 'pickupDate', control })
  const radiusValue = useWatch<number | undefined>({ name: 'radius', control }) || undefined
  const haulTypeValue = useWatch<string | undefined>({ name: 'haulType', control })
  const deliveryValue = useWatch<ILocationSuggestion | undefined>({ name: 'delivery', control })
  const dropoffPreferenceValue = useWatch<string>({ name: 'dropoffPreference', control })

  const introStep = (
    <Box
      p={4}
      minHeight='50vh'
      display='flex'
      alignItems='center'
      flexDirection='column'
      justifyContent='center'>
      <TruckAvailabilityIllustration />
      <Typography variant='h2' className='pv4 tc'>
        Create your first truck posting
      </Typography>
      <Typography variant='body2' color='textSecondary' align='center'>
        Add your trucks’ availability to the truck availability planner to be notified when loads
        that match your trucks are available. After you add you trucks, you can share truck
        availability with other carriers.
      </Typography>
    </Box>
  )

  const dropoffSection = (
    <Box py={3}>
      <Controller
        name='delivery'
        control={control}
        render={({ onChange, value = deliveryValue }) => {
          const changeHandler = (change: ILocationSuggestion) => {
            onChange(change)
          }
          return (
            <SuggestLocation
              large
              value={value}
              label='Dropoff Location'
              countries={['US', 'CA', 'MX']}
              placeholder='Enter a city, state, or zip'
              resultTypes={['City', 'State', 'PostalCode']}
              onChange={changeHandler}
              dataTest='dropoff-location'
            />
          )
        }}
      />
    </Box>
  )

  const haulSection = (
    <Box pt={1}>
      <Controller
        name='haulType'
        control={control}
        render={({ onChange, value }) => {
          const changeHandler = (change: string) => {
            onChange(change)
          }
          return (
            <CardButtonGroup
              direction={isDesktop ? 'row' : 'column'}
              value={value}
              onClick={changeHandler}>
              <CardButton line1='Local hauls' line2='up to 100 mi' value='local' />
              <CardButton line1='Short hauls' line2='100 mi - 300 mi' value='short' />
              <CardButton line1='Long hauls' line2='over 300 mi' value='long' />
            </CardButtonGroup>
          )
        }}
      />
    </Box>
  )

  const formStep = (
    <>
      <Typography variant='body1' color='textSecondary'>
        Enter the information below to be notified about matching loads for this truck
      </Typography>

      <Typography variant='subtitle1' className='pt6 pb4'>
        Select Pickup Preferences
      </Typography>

      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='flex-start'
        flexDirection={isDesktop ? 'row' : 'column'}>
        <Box className={isDesktop ? 'pr1' : 'pt4'} width={isDesktop ? 'unset' : '100%'}>
          <label className='label' htmlFor='pickup-date-filter'>
            Pickup Date
          </label>
          <Controller
            name='pickupDate'
            control={control}
            render={({ onChange, value = pickupDateProp }) => {
              const changeHandler = (change: string[]) => {
                onChange(change[0])
              }
              return (
                <DatePicker
                  large
                  date={value}
                  placeholder='Select a date'
                  id='pickup-date-filter'
                  onDateChange={changeHandler}
                  invalid={!!errors.pickupDate}
                  validationMessage={errors.pickupDate && 'Pickup date is required'}
                  dataTest='form-pickup-date'
                />
              )
            }}
          />
        </Box>
        <Box className={isDesktop ? 'pr1' : 'pt4'} width={isDesktop ? '50%' : '100%'}>
          <Controller
            name='pickup'
            control={control}
            render={({ onChange, value }) => {
              const changeHandler = (change: ILocationSuggestion | undefined) => {
                onChange(change)
              }
              return (
                <SuggestLocation
                  large
                  value={value}
                  label='Pickup Location'
                  countries={['US', 'CA', 'MX']}
                  placeholder='Enter a city, state, or zip'
                  resultTypes={['City', 'State', 'PostalCode']}
                  errorMessage={errors.pickup ? 'Pickup location is required' : undefined}
                  onChange={changeHandler}
                  dataTest='form-pickup-location'
                />
              )
            }}
          />
        </Box>
        <Box className={isDesktop ? '' : 'pt4'} width={isDesktop ? '150px' : '100%'}>
          <Controller
            name='radius'
            control={control}
            render={({ onChange, value }) => {
              return (
                <Select
                  large
                  label='Radius (Optional)'
                  placeholder='Select'
                  value={value}
                  onChange={onChange}
                  options={RADIUS_OPTIONS}
                  dataTest='form-radius'
                />
              )
            }}
          />
        </Box>
      </Box>

      <Typography variant='subtitle1' className='pt6'>
        Select Dropoff Preference
      </Typography>

      <Controller
        name='dropoffPreference'
        control={control}
        render={({ onChange, value }) => {
          const changeHandler = (change: string) => {
            onChange(change)
            setValue('delivery', undefined)
            setValue('haulType', undefined)
          }
          return (
            <ButtonGroup value={value} onChange={changeHandler}>
              <Button size='large' value='haul'>
                Haul Length
              </Button>
              <Button size='large' value='dropoff'>
                Dropoff Location
              </Button>
            </ButtonGroup>
          )
        }}
      />
      <Box height='120px'>
        {dropoffPreferenceValue === 'haul' && haulSection}
        {dropoffPreferenceValue === 'dropoff' && dropoffSection}
      </Box>
    </>
  )

  const confirmSection = (
    <Box p={2} className={classes.confirmBox} minWidth='50%'>
      <Typography variant='subtitle1'>Truck Posting</Typography>
      <Box display='flex' pt={3}>
        <Typography variant='body2' color='textSecondary'>
          <Box display='flex' flexDirection='column' pr={4} lineHeight={2}>
            <span>Pickup Date</span>
            <span>Pickup Location</span>
            <span>Dropoff Preference</span>
          </Box>
        </Typography>
        <Typography variant='body2'>
          <Box display='flex' flexDirection='column' lineHeight={2} flexGrow={1}>
            <span>{formatDate(pickupDateValue)}</span>
            <Box display='flex' alignItems='center'>
              {formatLocation(pickupValue)}
              {!!radiusValue && (
                <Box pl={1}>
                  <Typography color='textSecondary'>{`+${radiusValue} radius`}</Typography>
                </Box>
              )}
            </Box>
            <span>{formatLocation(deliveryValue, haulTypeValue)}</span>
          </Box>
        </Typography>
      </Box>
    </Box>
  )

  const confirmSectionMobile = (
    <Box p={2} className={classes.confirmBox} width='100%'>
      <Typography variant='subtitle1'>Truck Posting</Typography>
      <Typography variant='body2' color='textSecondary' className='pt4'>
        Pickup Date
      </Typography>
      <Typography variant='body2'>{formatDate(pickupDateValue)}</Typography>
      <Typography variant='body2' color='textSecondary' className='pt4'>
        Pickup Location
      </Typography>
      <Box display='flex' alignItems='center'>
        <Typography variant='body2'>{formatLocation(pickupValue)}</Typography>
        {!!radiusValue && (
          <Typography color='textSecondary'>{` +${radiusValue} radius`}</Typography>
        )}
      </Box>
      <Typography variant='body2' color='textSecondary' className='pt4'>
        Dropoff Preference
      </Typography>
      <Typography variant='body2'>{formatLocation(deliveryValue, haulTypeValue)}</Typography>
    </Box>
  )

  const confirmStep = (
    <>
      <Typography variant='body1' color='textSecondary'>
        Once you confirm, we’ll add this truck to your availability planner and begin notifying you
        as matching loads become available for you to book.
      </Typography>

      {isMobile ? confirmSectionMobile : confirmSection}
    </>
  )

  const stepData = [
    {
      title: '',
      body: introStep,
      label: 'Next',
    },
    {
      title: 'Post your truck',
      body: formStep,
      label: 'Next',
      validate: true,
    },
    {
      title: 'Ready to post your truck?',
      body: confirmStep,
      label: 'Confirm & Post',
      validate: false,
    },
  ]

  const createBookableShipmentSavedSearch = (): BookableShipmentSavedSearch => {
    let loadedMilesMin: number | undefined
    let loadedMilesMax: number | undefined
    if (haulTypeValue === 'local') {
      loadedMilesMin = HAUL_TYPE_MILES.local.min
      loadedMilesMax = HAUL_TYPE_MILES.local.max
    } else if (haulTypeValue === 'short') {
      loadedMilesMin = HAUL_TYPE_MILES.short.min
      loadedMilesMax = HAUL_TYPE_MILES.short.max
    } else if (haulTypeValue === 'long') {
      loadedMilesMin = HAUL_TYPE_MILES.long.min
      loadedMilesMax = HAUL_TYPE_MILES.long.max
    }
    return pickBy(
      {
        sourceType: 'dispatcher_truck_availability',
        pickupCity: pickupValue?.city,
        pickupStateCode: pickupValue?.state,
        pickupMiles: radiusValue || undefined,
        pickupStartDate: pickupDateValue,
        deliveryCity: deliveryValue?.city,
        deliveryStateCode: deliveryValue?.state,
        loadedMilesMin,
        loadedMilesMax,
      },
      v => v !== undefined
    )
  }

  const nextStepHandler = async () => {
    if (stepData[activeStepIdx].validate && !(await trigger())) {
      // disable progression if validation fails
      return
    }
    if (activeStepIdx === stepData.length - 1) {
      // commit the saved search if we're on the last step
      trackEvent(
        'Truck Availability',
        'SUBMITTED_TRUCK_AVAILABILITY_IN_POST_TRUCK_AVAILABILITY_MODAL'
      )
      onConfirmProp(createBookableShipmentSavedSearch())
    } else {
      // otherwise, go to the next step
      setActiveStepIdx(activeStepIdx => Math.min(activeStepIdx + 1, stepData.length - 1))
    }
  }

  return (
    <FormModal
      size={6}
      open
      onClose={onCloseProp}
      title={stepData[activeStepIdx].title}
      titleSize={2}
      actions={[
        {
          type: 'dismiss',
          label: 'Cancel',
          dataTest: 'cancel-button',
          action: onCloseProp,
        },
        {
          type: 'confirm',
          label: stepData[activeStepIdx].label,
          dataTest: 'confirm-button',
          action: nextStepHandler,
        },
      ]}>
      {stepData[activeStepIdx].body}
    </FormModal>
  )
}
